// src/components/admin/nornsweave/modals/ClosureModal.js
import React from 'react';
import TimePicker, { formatTime12 } from '../../../TimePicker';
import { CLOSURE_REASONS, useAdminNornsWeave } from '../AdminNornsWeaveContext';

function ClosureModal({
  closureForm,
  setClosureForm,
  affectedBookings,
  updateAffectedBookings,
  onClose,
  onSave
}) {
  const {
    settingsForm,
    getRecessTeachers,
    getTeachersForRecess,
    getAffectedRecesses,
    doesClosureAffectRecess,
    getAidesForDayType,
    getAdminContactsForType,
    getLibraryChairs,
    getLibraryAssistant
  } = useAdminNornsWeave();

  // Derived values
  const libraryChairSettings = settingsForm.libraryChairSettings;
  const libraryAssistant = getLibraryAssistant();
  const libraryChairs = getLibraryChairs();
  const adminContactsForClosures = getAdminContactsForType('closures');
  const aidesForDay = getAidesForDayType(closureForm.dayType);

  const affectedRecesses = getAffectedRecesses(
    closureForm.startTime || '00:00',
    closureForm.endTime || '23:59',
    closureForm.library,
    closureForm.timeType
  );
  const showRecessWarning = affectedRecesses.length > 0;

  // Calculate notification count
  const getNotificationCount = () => {
    if (closureForm.notifyOption === 'none') return 0;
    let count = affectedBookings.length;
    if (closureForm.notifyAides && aidesForDay.length > 0) count += aidesForDay.length;
    // Count recess teachers per enabled recess
    if (showRecessWarning) {
      const notifyMap = closureForm.notifyRecessMap || {};
      affectedRecesses.forEach(recess => {
        if (notifyMap[recess.id] !== false) {
          count += getTeachersForRecess(recess).length;
        }
      });
    }
    if (libraryAssistant && closureForm.timeType === 'full' && closureForm.notifyLibraryAssistant) count += 1;
    if (closureForm.notifyLibraryChairs && closureForm.notifyOption === 'immediate' && libraryChairs.length > 0) count += libraryChairs.length;
    const adminCount = closureForm.adminContactIds?.length ?? adminContactsForClosures.length;
    count += adminCount;
    if (closureForm.additionalCcEmail) count += 1;
    return count;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          Close Library
        </h3>

        <div className="space-y-6">
          {/* Reason Selection */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">Reason</label>
            <div className="grid grid-cols-2 gap-2">
              {CLOSURE_REASONS.map(reason => (
                <label
                  key={reason.value}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    closureForm.reason === reason.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="closureReason"
                    value={reason.value}
                    checked={closureForm.reason === reason.value}
                    onChange={(e) => {
                      const newForm = {
                        ...closureForm,
                        reason: e.target.value,
                        notifyOption: e.target.value === 'sick_day' ? 'immediate' : closureForm.notifyOption
                      };
                      setClosureForm(newForm);
                      updateAffectedBookings(newForm);
                    }}
                    className="text-red-600"
                  />
                  <span className="text-sm">{reason.label}</span>
                </label>
              ))}
            </div>
            {closureForm.reason === 'other' && (
              <input
                type="text"
                placeholder="Enter reason..."
                className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-lg"
                value={closureForm.customReason}
                onChange={(e) => setClosureForm({ ...closureForm, customReason: e.target.value })}
              />
            )}
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">Date(s)</label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dateType"
                  value="single"
                  checked={closureForm.dateType === 'single'}
                  onChange={() => {
                    const newForm = { ...closureForm, dateType: 'single', endDate: '' };
                    setClosureForm(newForm);
                    updateAffectedBookings(newForm);
                  }}
                />
                <span className="text-sm">Single day</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dateType"
                  value="range"
                  checked={closureForm.dateType === 'range'}
                  onChange={() => setClosureForm({ ...closureForm, dateType: 'range' })}
                />
                <span className="text-sm">Multiple days</span>
              </label>
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                className="px-3 py-2 border border-slate-300 rounded-lg"
                value={closureForm.startDate}
                onChange={(e) => {
                  const newForm = { ...closureForm, startDate: e.target.value };
                  setClosureForm(newForm);
                  updateAffectedBookings(newForm);
                }}
              />
              {closureForm.dateType === 'range' && (
                <>
                  <span className="text-slate-500">to</span>
                  <input
                    type="date"
                    className="px-3 py-2 border border-slate-300 rounded-lg"
                    value={closureForm.endDate}
                    onChange={(e) => {
                      const newForm = { ...closureForm, endDate: e.target.value };
                      setClosureForm(newForm);
                      updateAffectedBookings(newForm);
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">Time</label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="timeType"
                  value="full"
                  checked={closureForm.timeType === 'full'}
                  onChange={() => {
                    const newForm = { ...closureForm, timeType: 'full', startTime: '', endTime: '' };
                    setClosureForm(newForm);
                    updateAffectedBookings(newForm);
                  }}
                />
                <span className="text-sm">Full day (all bookings cancelled)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="timeType"
                  value="partial"
                  checked={closureForm.timeType === 'partial'}
                  onChange={() => setClosureForm({ ...closureForm, timeType: 'partial' })}
                />
                <span className="text-sm">Partial day</span>
              </label>
            </div>
            {closureForm.timeType === 'partial' && (
              <div className="flex gap-2 items-center">
                <TimePicker
                  value={closureForm.startTime}
                  onChange={(val) => {
                    const newForm = { ...closureForm, startTime: val };
                    setClosureForm(newForm);
                    updateAffectedBookings(newForm);
                  }}
                />
                <span className="text-slate-500">to</span>
                <TimePicker
                  value={closureForm.endTime}
                  onChange={(val) => {
                    const newForm = { ...closureForm, endTime: val };
                    setClosureForm(newForm);
                    updateAffectedBookings(newForm);
                  }}
                />
              </div>
            )}
          </div>

          {/* Library Selection */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">Library affected</label>
            <div className="flex gap-4">
              {['elementary', 'middle', 'both'].map(lib => (
                <label key={lib} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="library"
                    value={lib}
                    checked={closureForm.library === lib}
                    onChange={(e) => {
                      const newForm = { ...closureForm, library: e.target.value };
                      setClosureForm(newForm);
                      updateAffectedBookings(newForm);
                    }}
                  />
                  <span className="text-sm capitalize">{lib === 'both' ? 'Both' : lib === 'elementary' ? 'Elementary' : 'Middle School'}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Day Type Selection */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">Day Type (for aide schedules)</label>
            <div className="flex gap-4">
              {['A', 'B', 'na'].map(type => (
                <label key={type} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dayType"
                    value={type}
                    checked={closureForm.dayType === type}
                    onChange={(e) => setClosureForm({ ...closureForm, dayType: e.target.value })}
                  />
                  <span className="text-sm">
                    {type === 'A' ? 'A Day' : type === 'B' ? 'B Day' : 'Not Applicable'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Affected Bookings Preview */}
          {affectedBookings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2">
                AFFECTED BOOKINGS: {affectedBookings.length}
              </h4>
              <ul className="space-y-1 text-sm text-amber-700">
                {affectedBookings.slice(0, 5).map(booking => (
                  <li key={booking.id}>
                    • {booking.teacherName} - {booking.grade || 'Class'} ({formatTime12(booking.startTime)})
                  </li>
                ))}
                {affectedBookings.length > 5 && (
                  <li className="text-amber-600">...and {affectedBookings.length - 5} more</li>
                )}
              </ul>
            </div>
          )}

          {/* Recess Impact */}
          {showRecessWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                Recess Impact
              </h4>
              <div className="space-y-3">
                {affectedRecesses.map(recess => {
                  const recessTeachers = getTeachersForRecess(recess);
                  const notifyMap = closureForm.notifyRecessMap || {};
                  const isChecked = notifyMap[recess.id] !== false;
                  return (
                    <div key={recess.id}>
                      <p className="text-sm text-amber-700 mb-1">
                        <strong>{recess.description || 'Recess'}</strong> ({formatTime12(recess.startTime)} - {formatTime12(recess.endTime)})
                        {recessTeachers.length > 0 && ` — ${recessTeachers.length} teacher${recessTeachers.length !== 1 ? 's' : ''}`}
                      </p>
                      {recessTeachers.length > 0 && (
                        <label className="flex items-center gap-2 ml-2">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => setClosureForm({
                              ...closureForm,
                              notifyRecessMap: { ...notifyMap, [recess.id]: e.target.checked }
                            })}
                            className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-amber-800">
                            Notify teachers (Grades {recess.grades?.join(', ')})
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aide Instructions */}
          {closureForm.dayType && closureForm.dayType !== 'na' && (
            <div className={`border rounded-lg p-4 ${
              closureForm.dayType === 'A' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'
            }`}>
              <h4 className={`font-semibold mb-3 flex items-center gap-2 ${
                closureForm.dayType === 'A' ? 'text-blue-800' : 'text-purple-800'
              }`}>
                {closureForm.dayType} Day Aide Instructions
              </h4>

              {/* Show aides on duty */}
              <div className="mb-3">
                <p className="text-sm text-slate-600 mb-1">Aides on duty:</p>
                {aidesForDay.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No aides assigned to {closureForm.dayType} Days</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {aidesForDay.map(aide => (
                      <span key={aide.id} className={`text-sm px-2 py-1 rounded ${
                        closureForm.dayType === 'A' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                      }`}>
                        {aide.name} {aide.email && `(${aide.email})`}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {aidesForDay.length > 0 && (
                <>
                  <label className="flex items-center gap-2 mb-3">
                    <input
                      type="checkbox"
                      checked={closureForm.notifyAides ?? true}
                      onChange={(e) => setClosureForm({ ...closureForm, notifyAides: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Email aides with instructions</span>
                  </label>

                  {closureForm.notifyAides && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-slate-600 mb-1">Work assignment (optional)</label>
                        <input
                          type="text"
                          placeholder="e.g., Shelf reading in fiction section"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          value={closureForm.aideWorkAssignment || ''}
                          onChange={(e) => setClosureForm({ ...closureForm, aideWorkAssignment: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-600 mb-1">Report to (optional)</label>
                        <input
                          type="text"
                          placeholder="e.g., Front office"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          value={closureForm.aideReportTo || ''}
                          onChange={(e) => setClosureForm({ ...closureForm, aideReportTo: e.target.value })}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Library Assistant */}
          {libraryAssistant && closureForm.timeType === 'full' && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={closureForm.notifyLibraryAssistant ?? true}
                  onChange={(e) => setClosureForm({ ...closureForm, notifyLibraryAssistant: e.target.checked })}
                  className="rounded border-teal-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-teal-800">
                  Notify Library Assistant: {libraryAssistant.name} ({libraryAssistant.email})
                </span>
              </label>
            </div>
          )}

          {/* Library Chairs - Emergency Closure */}
          {libraryChairs.length > 0 && closureForm.notifyOption === 'immediate' && libraryChairSettings?.notifyOnEmergencyClosure && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={closureForm.notifyLibraryChairs ?? true}
                  onChange={(e) => setClosureForm({ ...closureForm, notifyLibraryChairs: e.target.checked })}
                  className="rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-violet-800">
                  Notify Library Chairs ({libraryChairs.length}): {libraryChairs.map(c => c.name).join(', ')}
                </span>
              </label>
            </div>
          )}

          {/* Admin CC */}
          {adminContactsForClosures.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
              <h4 className="font-semibold text-rose-800 mb-2">Admin Notifications</h4>
              <div className="space-y-2">
                {adminContactsForClosures.map(contact => {
                  const isSelected = closureForm.adminContactIds
                    ? closureForm.adminContactIds.includes(contact.id)
                    : true; // Default to selected
                  return (
                    <label key={contact.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const currentIds = closureForm.adminContactIds || adminContactsForClosures.map(c => c.id);
                          const newIds = e.target.checked
                            ? [...currentIds.filter(id => id !== contact.id), contact.id]
                            : currentIds.filter(id => id !== contact.id);
                          setClosureForm({ ...closureForm, adminContactIds: newIds });
                        }}
                        className="rounded border-rose-300 text-rose-600 focus:ring-rose-500"
                      />
                      <span className="text-sm text-rose-800">
                        CC: {contact.name} ({contact.email})
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* One-time additional recipient */}
              <div className="mt-3 pt-3 border-t border-rose-200">
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Add one-time CC email"
                    className="flex-1 px-3 py-1.5 border border-rose-200 rounded text-sm"
                    value={closureForm.additionalCcEmail || ''}
                    onChange={(e) => setClosureForm({ ...closureForm, additionalCcEmail: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notification Timing */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">When to notify</label>
            <div className="space-y-2">
              {closureForm.reason === 'sick_day' ? (
                <p className="text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded flex items-center gap-2">
                  <span>⚡</span> Notifications will be sent immediately (emergency closure)
                </p>
              ) : (
                <>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="notifyOption"
                      value="immediate"
                      checked={closureForm.notifyOption === 'immediate'}
                      onChange={(e) => setClosureForm({ ...closureForm, notifyOption: e.target.value })}
                    />
                    <span className="text-sm">Send immediately</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="notifyOption"
                      value="1week"
                      checked={closureForm.notifyOption === '1week'}
                      onChange={(e) => setClosureForm({ ...closureForm, notifyOption: e.target.value })}
                    />
                    <span className="text-sm">Schedule: 1 week before</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="notifyOption"
                      value="none"
                      checked={closureForm.notifyOption === 'none'}
                      onChange={(e) => setClosureForm({ ...closureForm, notifyOption: e.target.value })}
                    />
                    <span className="text-sm">Don't send notifications (I'll tell them myself)</span>
                  </label>
                </>
              )}
            </div>
          </div>

          {/* Custom Message */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">Email message (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              rows={3}
              placeholder={closureForm.reason === 'sick_day'
                ? "The library is unexpectedly closed today. Apologies for any inconvenience. Please reschedule your visit."
                : "Add a personal note to teachers..."}
              value={closureForm.customMessage}
              onChange={(e) => setClosureForm({ ...closureForm, customMessage: e.target.value })}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {closureForm.notifyOption === 'none'
              ? 'Close Library'
              : `Close Library & Notify ${getNotificationCount()} Recipient${getNotificationCount() !== 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClosureModal;
